import React, { useEffect, useMemo, useState } from 'react';
import { RotateCcwIcon } from 'lucide-react';
import { SiteLayout } from '../components/SiteLayout';
import { endMillFeedsSpeedsContent } from '../data/technicalData';
type Unit = 'inch' | 'metric';
type MaterialGroup = {
  id: string;
  label: string;
};
type Family = {
  id: string;
  name: string;
  groupId: string;
};
type Parameter = {
  speedIn: [number, number];
  speedMetric: [number, number];
  feedIn: number[];
  feedMetric: number[];
};
type OutputValueProps = {
  label: string;
  low: string;
  high: string;
  current?: string;
};
const inchDiameters = [{
  value: 0.125,
  label: '1/8"'
}, {
  value: 0.188,
  label: '3/16"'
}, {
  value: 0.25,
  label: '1/4"'
}, {
  value: 0.313,
  label: '5/16"'
}, {
  value: 0.375,
  label: '3/8"'
}, {
  value: 0.5,
  label: '1/2"'
}, {
  value: 0.625,
  label: '5/8"'
}, {
  value: 0.75,
  label: '3/4"'
}, {
  value: 1,
  label: '1"'
}];
const metricDiameters = [3, 5, 6, 8, 10, 12, 16, 20, 25];
const groups: MaterialGroup[] = [{
  id: '1',
  label: 'Steel (P)'
}, {
  id: '2',
  label: 'Cast Iron (K)'
}, {
  id: '3',
  label: 'Stainless Steel (M)'
}, {
  id: '4',
  label: 'Special Alloys (S)'
}, {
  id: '5',
  label: 'Hardened Steel (H)'
}, {
  id: '6',
  label: 'Non-Ferrous (N)'
}];
const families: Family[] = [['1', 'Low-Mid Alloy (10XX, 11XX, 13XX)', '1'], ['2', 'High Strength (4140, 4340, 6150, 5210)', '1'], ['3', 'High Alloy (A-2/6/10, P20, 01, 02, 06)', '1'], ['4', 'Forgings', '1'], ['5', 'Gray Cast Iron', '2'], ['6', 'Ductile Iron', '2'], ['7', 'Precipitation (13/8, 15/5, AM-350/355)', '3'], ['8', 'Austenitic (200, 302, 303, 304L, 316L)', '3'], ['9', 'Martensitic (403, 410, 416)', '3'], ['10', 'Titanium (6AL-V4, All)', '4'], ['11', 'Co-Based (Stelite, HS-21, Haynes 25/188, X40, L-605)', '4'], ['12', 'Ni-Based (Inconel 600/625, Icoloy 600-800, Invar)', '4'], ['13', 'Fe-Based (Incoloy 800-802,N-155, Timken 16-26-6)', '4'], ['14', '35-45Rc', '5'], ['15', '45-55Rc', '5'], ['16', '55-65Rc', '5'], ['17', 'AL / AL Alloys', '6'], ['18', 'Copper - Brass / Bronze', '6'], ['19', 'Mg / Mg Alloys', '6'], ['20', 'Plastics / Acrylics / Phenolics / Bakelite', '6']].map(([id, name, groupId]) => ({
  id,
  name,
  groupId
}));
const rawParameters: Array<[string, [number, number], [number, number], number[], number[]]> = [['1', [100, 350], [30, 107], [.0005, .0007, .0012, .0015, .002, .003, .004, .005, .006], [.013, .018, .03, .038, .051, .076, .102, .127, .152]], ['2', [100, 350], [30, 107], [.0005, .0007, .001, .0015, .002, .0025, .003, .004, .005], [.013, .018, .025, .038, .051, .064, .076, .102, .127]], ['3', [100, 160], [30, 49], [.0004, .0006, .001, .0012, .0015, .002, .0025, .003, .004], [.01, .015, .025, .03, .038, .051, .064, .076, .102]], ['4', [100, 160], [30, 49], [.0004, .0006, .001, .0012, .0015, .002, .0025, .003, .004], [.01, .015, .025, .03, .038, .051, .064, .076, .102]], ['5', [120, 300], [37, 91], [.0005, .001, .002, .0025, .003, .004, .005, .006, .008], [.013, .025, .051, .064, .076, .102, .127, .152, .203]], ['6', [120, 350], [37, 107], [.0005, .0008, .0015, .0017, .002, .0025, .003, .004, .006], [.013, .02, .038, .043, .051, .064, .076, .102, .152]], ['7', [80, 250], [24, 76], [.0003, .0005, .001, .0012, .0015, .002, .003, .004, .005], [.008, .013, .025, .03, .038, .051, .076, .102, .127]], ['8', [100, 350], [30, 107], [.0002, .0003, .0005, .0007, .001, .002, .0025, .0035, .0045], [.005, .008, .013, .018, .025, .051, .064, .089, .114]], ['9', [80, 250], [24, 76], [.0002, .0003, .0005, .0007, .001, .0015, .0025, .003, .004], [.005, .008, .013, .018, .025, .038, .064, .076, .102]], ['10', [100, 250], [30, 76], [.0003, .0004, .0005, .0007, .001, .0015, .0025, .003, .004], [.008, .01, .013, .018, .025, .038, .064, .076, .102]], ['11', [50, 90], [15, 27], [.0003, .0004, .0006, .0007, .0008, .0012, .0015, .002, .0025], [.008, .01, .015, .018, .02, .03, .038, .051, .064]], ['12', [50, 95], [15, 29], [.0004, .0005, .0008, .0009, .001, .002, .0025, .003, .004], [.01, .013, .02, .023, .025, .051, .064, .076, .102]], ['13', [70, 125], [21, 38], [.0004, .0005, .0008, .0009, .001, .002, .0025, .003, .004], [.01, .013, .02, .023, .025, .051, .064, .076, .102]], ['14', [80, 150], [24, 46], [.0004, .0006, .001, .0012, .0015, .002, .0025, .003, .004], [.01, .015, .025, .03, .038, .051, .064, .076, .102]], ['15', [80, 150], [24, 46], [.0004, .0006, .001, .0012, .0015, .002, .0025, .003, .004], [.01, .015, .025, .03, .038, .051, .064, .076, .102]], ['16', [60, 110], [18, 34], [.0004, .0006, .001, .0012, .0015, .002, .0025, .003, .004], [.01, .015, .025, .03, .038, .051, .064, .076, .102]], ['17', [600, 1500], [183, 457], [.001, .002, .003, .0035, .004, .005, .006, .008, .01], [.025, .051, .076, .089, .102, .127, .152, .203, .254]], ['18', [800, 1000], [244, 305], [.001, .0012, .002, .0022, .0025, .003, .004, .0045, .005], [.025, .03, .051, .056, .064, .076, .102, .114, .127]], ['19', [800, 1500], [244, 457], [.001, .0015, .002, .0025, .003, .004, .006, .008, .009], [.025, .038, .051, .064, .076, .102, .152, .203, .229]], ['20', [200, 500], [61, 152], [.001, .002, .003, .0035, .004, .006, .008, .01, .012], [.025, .051, .076, .089, .102, .152, .203, .254, .305]]];
const parameters: Record<string, Parameter> = Object.fromEntries(rawParameters.map(([id, speedIn, speedMetric, feedIn, feedMetric]) => [id, {
  speedIn,
  speedMetric,
  feedIn,
  feedMetric
}]));
const coatings = [{
  id: '1',
  label: 'AlTiN',
  modifier: 1.35
}, {
  id: '2',
  label: 'TiCN',
  modifier: 1.25
}, {
  id: '3',
  label: 'TiN',
  modifier: 1.25
}, {
  id: '4',
  label: 'Uncoated',
  modifier: 1
}];
export function EndMillFeedsSpeedsCalculatorPage() {
  const [unit, setUnit] = useState<Unit>('inch');
  const [diameterIndex, setDiameterIndex] = useState(0);
  const [teeth, setTeeth] = useState(2);
  const [coatingId, setCoatingId] = useState('1');
  const [groupId, setGroupId] = useState('');
  const [familyId, setFamilyId] = useState('');
  const [radialDepth, setRadialDepth] = useState('');
  const [length, setLength] = useState('');
  const [speed, setSpeed] = useState('');
  const [feed, setFeed] = useState('');
  useEffect(() => {
    document.title = 'Cutting Feed Rate and Spindle Speed Calculator for Milling';
  }, []);
  const visibleFamilies = families.filter(family => family.groupId === groupId);
  const diameter = unit === 'inch' ? inchDiameters[diameterIndex].value : metricDiameters[diameterIndex];
  const parameter = parameters[familyId];
  const modifier = coatings.find(coating => coating.id === coatingId)?.modifier ?? 1;
  const output = useMemo(() => calculate({
    unit,
    diameter,
    teeth,
    modifier,
    parameter,
    radialDepth: Number(radialDepth),
    length: Number(length),
    speed: Number(speed),
    feed: Number(feed),
    diameterIndex
  }), [unit, diameter, teeth, modifier, parameter, radialDepth, length, speed, feed, diameterIndex]);
  const unitLabels = unit === 'inch' ? {
    cut: 'Inches',
    surface: 'Surface Feet per Minute (SFM)',
    tooth: 'Inches per Tooth (IPT)',
    rev: 'Inches per Revolution (IPR)',
    rate: 'Inches per Minute (IPM)'
  } : {
    cut: 'Millimeters',
    surface: 'Surface Meters per Minute (SMM)',
    tooth: 'Millimeters per Tooth (MMPT)',
    rev: 'Millimeters per Revolution (MMPR)',
    rate: 'Millimeters per Minute (MMPM)'
  };
  const onUnitChange = (next: Unit) => {
    setUnit(next);
    setDiameterIndex(0);
  };
  const onGroupChange = (next: string) => {
    setGroupId(next);
    setFamilyId(families.find(family => family.groupId === next)?.id ?? '');
  };
  const reset = () => {
    setDiameterIndex(0);
    setTeeth(2);
    setCoatingId('1');
    setGroupId('');
    setFamilyId('');
    setRadialDepth('');
    setLength('');
    setSpeed('');
    setFeed('');
  };
  const inputClass = 'mt-1.5 w-full rounded border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-brand-charcoal outline-none focus:border-brand-darkGold focus:ring-1 focus:ring-brand-darkGold';
  return <SiteLayout data-id="element-888" data-mp="6kjt3r8eox7"><main className="mx-auto max-w-6xl px-4 py-10 md:py-14" data-id="element-889" data-mp="6aulie0glsk"><p className="text-sm font-bold tracking-wide text-brand-red" data-id="element-890" data-mp="3ridk464dzxmr">TECHNICAL INFO</p><h1 className="mt-2 text-3xl font-bold text-brand-charcoal md:text-4xl" data-id="element-891" data-mp="94d12dgik6n8">Feeds and Speeds Calculator for Milling</h1><h2 className="mt-3 text-xl font-bold text-brand-charcoal md:text-2xl" data-id="element-892" data-mp="451j514euv42">Easily Calculate SFM, RPM, and Feed Rate</h2><div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-semibold" data-id="element-893" data-mp="j2tvjkenv3e1"><a href="#instructions" className="text-brand-red underline" data-id="element-894" data-mp="e5k98dmkj7uf">Need Help?</a><span data-id="element-895" data-mp="2ku6za64w758i">Purchase Your <a href="/milling/end-mills.html" target="_blank" rel="noreferrer" className="text-brand-red underline" data-id="element-896" data-mp="2kvqd7q32t474">End Milling Cutters</a> Here!</span></div><section className="mt-7 rounded-lg border-2 border-brand-darkGold bg-brand-gray p-4 md:p-5" data-id="element-897" data-mp="2fxyo13ttfw6m"><div className="grid gap-4 lg:grid-cols-2" data-id="element-898" data-mp="1u0c630y8z6e0"><InputSection title="1) Cutting Tool Information" data-id="element-899" data-mp="1z0ztzdr6j740"><Field label="Unit Type" data-id="element-900" data-mp="1yzow1ukf0tkt"><select value={unit} onChange={event => onUnitChange(event.target.value as Unit)} className={inputClass} data-id="element-901" data-mp="23s9jmecqaafw"><option value="inch" data-id="element-902" data-mp="28rco9xosqenc">Inch</option><option value="metric" data-id="element-903" data-mp="28qb2ay68s7qu">Metric</option></select></Field><Field label="Tool Diameter" data-id="element-904" data-mp="28t5mcolcfqjq"><select value={diameterIndex} onChange={event => setDiameterIndex(Number(event.target.value))} className={inputClass} data-id="element-905" data-mp="1b43xpo9xufeh">{unit === 'inch' ? inchDiameters.map((item, index) => <option key={item.label} value={index} data-id="element-906" data-mp="1g4i6x9n9zve3" data-mpi={index}>{item.label}</option>) : metricDiameters.map((item, index) => <option key={item} value={index} data-id="element-907" data-mp="1l44izfrdb5lb" data-mpi={index}>{item}mm</option>)}</select></Field><Field label="# of Teeth" data-id="element-908" data-mp="1p4wuygrmjola"><select value={teeth} onChange={event => setTeeth(Number(event.target.value))} className={inputClass} data-id="element-909" data-mp="1p10ojpq23wvd">{Array.from({
                  length: 11
                }, (_, index) => index + 2).map(value => <option key={value} data-id="element-910" data-mp="30gxo07vitz6u">{value}</option>)}</select></Field><Field label="Tool Material" data-id="element-911" data-mp="oux75z1x6r9q"><select value="carbide" disabled className={inputClass} data-id="element-912" data-mp="ou3ygsjehroy"><option value="carbide" data-id="element-913" data-mp="ktk1boytuo2t">Carbide</option></select></Field><Field label="Tool Coating" data-id="element-914" data-mp="kuv7fvfqw7x3"><select value={coatingId} onChange={event => setCoatingId(event.target.value)} className={inputClass} data-id="element-915" data-mp="kvocmrz22pd4">{coatings.map(coating => <option key={coating.id} value={coating.id} data-id="element-916" data-mp="b40g4cnn6i4s">{coating.label}</option>)}</select></Field></InputSection><InputSection title="2) Material To Be Cut" data-id="element-917" data-mp="64fjw9zuwnvr"><Field label="Material Group" data-id="element-918" data-mp="65gxal4chmgn"><select value={groupId} onChange={event => onGroupChange(event.target.value)} className={inputClass} data-id="element-919" data-mp="62ky6tgrgrqm"><option value="" data-id="element-920" data-mp="17xgpek6cxlj">&lt;Please Select&gt;</option>{groups.map(group => <option key={group.id} value={group.id} data-id="element-921" data-mp="3ta6j4p9clwum">{group.label}</option>)}</select></Field><Field label="Material Family" data-id="element-922" data-mp="3ob24hj3nn6ww"><select value={familyId} onChange={event => setFamilyId(event.target.value)} disabled={!groupId} className={inputClass} data-id="element-923" data-mp="3oc4xpxog9lfz"><option value="" data-id="element-924" data-mp="1rfnz9dsgu3gk">&lt;Please Select&gt;</option>{visibleFamilies.map(family => <option key={family.id} value={family.id} data-id="element-925" data-mp="1mjgu3n7w8vzo">{family.name}</option>)}</select></Field></InputSection><InputSection title="3) Cut Information" data-id="element-926" data-mp="277u4hfjsgk7j"><Field label={`Radial Depth of Cut (${unitLabels.cut})`} data-id="element-927" data-mp="22aaq8fn3c7fl"><input value={radialDepth} onChange={event => setRadialDepth(event.target.value)} inputMode="decimal" className={inputClass} data-id="element-928" data-mp="229sjuq6pscty" /></Field><Field label={`Length of Cut (${unitLabels.cut})`} data-id="element-929" data-mp="2g7phlpwwmpa7"><input value={length} onChange={event => setLength(event.target.value)} inputMode="decimal" className={inputClass} data-id="element-930" data-mp="2bca8tcdr7jkn" /></Field>{Number(radialDepth) > diameter / 2 && <p className="col-span-full rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-id="element-931" data-mp="1jk2tqgdwcf8d">Radial depth of cut should not exceed half of the selected tool diameter.</p>}</InputSection><OutputSection title="4) SFM Calculator" subtitle={`Cutting Speed — ${unitLabels.surface}`} values={{
            label: 'Your Speed',
            low: output.speedLow,
            high: output.speedHigh,
            current: speed
          }} onCurrentChange={setSpeed} inputClass={inputClass} data-id="element-932" data-mp="1skr7d9zdodkq" /><OutputSection title="5) SFM to RPM Calculator" subtitle="Spindle Speed — Revolutions per Minute (RPM)" values={{
            label: 'At Your Speed',
            low: output.rpmLow,
            high: output.rpmHigh,
            current: output.rpm
          }} data-id="element-933" data-mp="zv238nrdfxew" /><OutputSection title="6) Feed Rate Calculator" subtitle={`Feed Per Tooth — ${unitLabels.tooth}`} values={{
            label: 'Your Feed',
            low: output.feedToothLow,
            high: output.feedToothHigh,
            current: feed
          }} onCurrentChange={setFeed} inputClass={inputClass} extra={<><OutputValue label={`Feed Per Revolution — ${unitLabels.rev}`} low={output.feedRevLow} high={output.feedRevHigh} current={output.feedRev} data-id="element-935" data-mp="3v7fr2jlyduqk" /><OutputValue label={`Feed Rate — ${unitLabels.rate}`} low={output.feedRateLow} high={output.feedRateHigh} current={output.feedRate} data-id="element-936" data-mp="322gk0g71flj" /></>} data-id="element-934" data-mp="3gaox4pv1tb8c" /><OutputSection title="7) Cut Time Calculator" subtitle="Duration (in seconds)" values={{
            label: 'At Your Speed and Feed',
            low: output.secondsLow,
            high: output.secondsHigh,
            current: output.seconds
          }} extra={<OutputValue label="Duration (in minutes)" low={output.minutesLow} high={output.minutesHigh} current={output.minutes} data-id="element-938" data-mp="2eswlaqaqf75n" />} data-id="element-937" data-mp="3lbcmz87jjm0z" /></div><button type="button" onClick={reset} className="mt-5 inline-flex items-center gap-2 rounded border border-brand-darkGold bg-[#ffe6a8] px-4 py-2 text-sm font-bold text-brand-charcoal hover:bg-brand-darkGold" data-id="element-939" data-mp="1ur1q60n2f7b4"><RotateCcwIcon className="h-4 w-4" data-id="element-940" data-mp="1lxgc6jiq5u2r" />Reset Fields</button></section><p className="mt-5 text-sm leading-6 text-gray-600" data-id="element-941" data-mp="1gzex09of6ewk">This calculator is a tool. These calculations are based upon theoretical values and are only intended for planning purposes. Actual results will vary. No responsibility from 6gTools LLC is assumed.</p><Instructions data-id="element-942" data-mp="16znylwwqnfr7" /><section className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3" data-id="element-943" data-mp="16zwc9r0oqp3g">{endMillFeedsSpeedsContent.resources.map(group => <section key={group.title} className="border border-brand-darkGold bg-[#fff9ea] p-5" data-id="element-944" data-mp="131gsrx3tbyus"><h2 className="font-bold text-brand-charcoal" data-id="element-945" data-mp="y2dls749vik6">{group.title}</h2><div className="mt-3 flex flex-wrap gap-x-3 gap-y-2 text-sm" data-id="element-946" data-mp="2xclsiqbs3r4u">{group.links.map(link => <a key={link.label} href={link.href} className="text-brand-red underline" data-id="element-947" data-mp="2se0u8wg37hj1">{link.label}</a>)}</div></section>)}</section></main></SiteLayout>;
}
function calculate({
  unit,
  diameter,
  teeth,
  modifier,
  parameter,
  radialDepth,
  length,
  speed,
  feed,
  diameterIndex
}: {
  unit: Unit;
  diameter: number;
  teeth: number;
  modifier: number;
  parameter?: Parameter;
  radialDepth: number;
  length: number;
  speed: number;
  feed: number;
  diameterIndex: number;
}) {
  if (!parameter) return blankOutput();
  const speedRange = unit === 'inch' ? parameter.speedIn : parameter.speedMetric;
  const feedRange = unit === 'inch' ? parameter.feedIn : parameter.feedMetric;
  const coefficient = unit === 'inch' ? 3.82 : 318.06;
  const speedLowRaw = speedRange[0] * modifier;
  const speedHighRaw = speedRange[1] * modifier;
  const rpmLowRaw = speedLowRaw * coefficient / diameter;
  const rpmHighRaw = speedHighRaw * coefficient / diameter;
  const feedBase = feedRange[diameterIndex];
  const feedLowRaw = feedBase * .7;
  const feedHighRaw = feedBase * 1.3;
  const chip = chipFactor(radialDepth, diameter);
  const rateLowRaw = chip ? teeth * chip * rpmLowRaw * feedLowRaw : 0;
  const rateHighRaw = chip ? teeth * chip * rpmHighRaw * feedHighRaw : 0;
  const selectedSpeed = Number.parseInt(String(speed), 10);
  const selectedLength = Number.parseInt(String(length), 10);
  const rpmRaw = selectedSpeed ? selectedSpeed * coefficient / diameter : 0;
  const feedRevRaw = feed ? feed * teeth : 0;
  const rateRaw = chip && feed && rpmRaw ? teeth * chip * feed * rpmRaw : 0;
  const lowMinutes = selectedLength && rateLowRaw ? selectedLength / rateLowRaw : 0;
  const highMinutes = selectedLength && rateHighRaw ? selectedLength / rateHighRaw : 0;
  const minutes = selectedLength && rateRaw ? selectedLength / rateRaw : 0;
  return {
    speedLow: integer(speedLowRaw),
    speedHigh: integer(speedHighRaw),
    rpmLow: integer(rpmLowRaw),
    rpmHigh: integer(rpmHighRaw),
    feedToothLow: decimal(feedLowRaw),
    feedToothHigh: decimal(feedHighRaw),
    feedRevLow: decimal(feedLowRaw * teeth),
    feedRevHigh: decimal(feedHighRaw * teeth),
    feedRateLow: oneDecimal(rateLowRaw),
    feedRateHigh: oneDecimal(rateHighRaw),
    rpm: integer(rpmRaw),
    feedRev: decimal(feedRevRaw),
    feedRate: oneDecimal(rateRaw),
    secondsLow: lowMinutes ? integer(lowMinutes * 60) : '',
    secondsHigh: highMinutes ? integer(highMinutes * 60) : '',
    seconds: minutes ? integer(minutes * 60) : '',
    minutesLow: lowMinutes ? twoDecimal(lowMinutes) : '',
    minutesHigh: highMinutes ? twoDecimal(highMinutes) : '',
    minutes: minutes ? twoDecimal(minutes) : ''
  };
}
function chipFactor(radial: number, diameter: number) {
  if (!radial) return 0;
  const ratio = Math.round(radial / diameter * 100) / 100;
  const factors: Array<[number, number]> = [[.01, 5], [.05, 2.3], [.10, 1.8], [.15, 1.4], [.20, 1.2], [.30, 1.1], [.50, 1]];
  for (let index = 0; index < factors.length; index += 1) {
    if (factors[index][0] > ratio) return index ? factors[index - 1][1] : 0;
  }
  return 1;
}
function blankOutput() {
  return {
    speedLow: '',
    speedHigh: '',
    rpmLow: '',
    rpmHigh: '',
    feedToothLow: '',
    feedToothHigh: '',
    feedRevLow: '',
    feedRevHigh: '',
    feedRateLow: '',
    feedRateHigh: '',
    rpm: '',
    feedRev: '',
    feedRate: '',
    secondsLow: '',
    secondsHigh: '',
    seconds: '',
    minutesLow: '',
    minutesHigh: '',
    minutes: ''
  };
}
function integer(value: number) {
  return value ? String(Math.round(value)) : '';
}
function decimal(value: number) {
  return value ? String(Math.round(value * 100000) / 100000) : '';
}
function oneDecimal(value: number) {
  return value ? (Math.round(value * 10) / 10).toFixed(1) : '';
}
function twoDecimal(value: number) {
  return value ? (Math.round(value * 100) / 100).toFixed(2) : '';
}
function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <label className="text-sm font-semibold text-brand-charcoal" data-id="element-948" data-mp="33zft6rlygs0f">{label}{children}</label>;
}
function InputSection({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="rounded border border-gray-200 bg-white p-4" data-id="element-949" data-mp="1smlqnrrptdt1"><h2 className="border-b border-brand-darkGold pb-2 font-bold text-brand-charcoal" data-id="element-950" data-mp="1nmha61qt4j2t">{title}</h2><div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-951" data-mp="1notwer3llflo">{children}</div></section>;
}
function OutputSection({
  title,
  subtitle,
  values,
  onCurrentChange,
  inputClass,
  extra
}: {
  title: string;
  subtitle: string;
  values: {
    label: string;
    low: string;
    high: string;
    current?: string;
  };
  onCurrentChange?: (value: string) => void;
  inputClass?: string;
  extra?: React.ReactNode;
}) {
  return <section className="rounded border border-gray-200 bg-white p-4" data-id="element-952" data-mp="1s9jgxp0ozurc"><h2 className="font-bold text-brand-charcoal" data-id="element-953" data-mp="1w80hfpply6zs">{title}</h2><p className="mt-1 text-sm font-semibold text-gray-600" data-id="element-954" data-mp="2184vza6ygrnd">{subtitle}</p><div className="mt-4" data-id="element-955" data-mp="216acm0bc17le"><OutputValue {...values} onCurrentChange={onCurrentChange} inputClass={inputClass} data-id="element-956" data-mp="219y28odnp9uu" /></div>{extra && <div className="mt-5 space-y-5 border-t border-gray-200 pt-5" data-id="element-957" data-mp="265ddd8ka4rnk">{extra}</div>}</section>;
}
function OutputValue({
  label,
  low,
  high,
  current,
  onCurrentChange,
  inputClass
}: OutputValueProps & {
  onCurrentChange?: (value: string) => void;
  inputClass?: string;
}) {
  return <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_92px_92px_112px]" data-id="element-958" data-mp="gstgynmv3z66"><span className="self-center text-sm font-semibold text-brand-charcoal" data-id="element-959" data-mp="2ioivfecfh5yi">{label}</span><ResultField label="Low Range" value={low} data-id="element-960" data-mp="2drio703udx5t" /><ResultField label="High Range" value={high} data-id="element-961" data-mp="2domr1dztjcif" />{onCurrentChange ? <label className="text-xs font-semibold text-gray-600" data-id="element-962" data-mp="2selm37w2u7to">{label}<input value={current ?? ''} onChange={event => onCurrentChange(event.target.value)} inputMode="decimal" className={inputClass} data-id="element-963" data-mp="2ngk6wy1rusnh" /></label> : <ResultField label="At Your Speed and Feed" value={current ?? ''} data-id="element-964" data-mp="1zrr3kgitliiu" />}</div>;
}
function ResultField({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <label className="text-xs font-semibold text-gray-600" data-id="element-965" data-mp="28re6j6r76we0">{label}<output className="mt-1.5 block min-h-[38px] rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-bold text-brand-charcoal" data-id="element-966" data-mp="28sz0ov9lodbc">{value || '—'}</output></label>;
}
function Instructions() {
  return <section id="instructions" className="mt-12 max-w-5xl scroll-mt-24" data-id="element-967" data-mp="3k8w23iymkppg"><h2 className="border-b-2 border-brand-darkGold pb-2 text-2xl font-bold text-brand-charcoal" data-id="element-968" data-mp="3g469c2z9jr8r">Milling Calculator Instructions</h2><div className="mt-4 space-y-5 leading-7 text-gray-700" data-id="element-969" data-mp="3nnfipw7rrpxt"><section data-id="element-970" data-mp="3so3fkyioj0zy"><h3 className="font-bold text-brand-charcoal" data-id="element-971" data-mp="3sov09eaqnebg">General:</h3><p data-id="element-972" data-mp="3dqkj19zrfjhy">This calculator is designed to coincide with standard feed and speed charts for various materials and carbide end mills. In the future we will be adding data for HSS and cobalt end mills.</p><p data-id="element-973" data-mp="3irgv4au9mbyg">Therefore the diameters that are presented are the most common diameters in the range of 1/8&quot; - 1&quot; and 3mm - 25mm.</p><p data-id="element-974" data-mp="f9gk0mtexqgm">Your job is to fill in the Blue boxes,</p><p data-id="element-975" data-mp="f85h0p7skmqk">The Green Boxes are calculated.</p></section><Instruction title="Section 1: Cutting Tool Information" text="Choose whether you are working in inch or metric units. Choose the diameter (DIA) closest to the specific one you are using. Choose the number of teeth (N) (flutes or cutting edges). Choose the cutting tool material. Currently this is just carbide. HSS and Cobalt materials are coming soon. Choose the tool coating." data-id="element-976" data-mp="kkfv0uh8xla" /><Instruction title="Section 2: Material To Be Cut" text="Choose the material group you are machining. Choose the material family from that group. Data from section 1 & 2 will be used to calculate the surface speed." data-id="element-977" data-mp="2pkiyg8p0wv18" /><Instruction title="Section 3: Cut Information" text="Enter the radial depth of cut (RDOC). The maximum number can 1/2 of the DIA. Enter the length of cut (LOC). This is optional. It is used to calculate the cutting time." data-id="element-978" data-mp="2fv7t8x59caim" /><Instruction title="Section 4: Surface Speed (SFM) Calculator" text="The speed ranges in surface feet per minute (SFM) or surface meters per minute (SMM) have been calculated. Choose your speed from that range." data-id="element-979" data-mp="26syic662uf4p" /><Instruction title="Section 5: RPM Calculator" text="This section is calculated from Your Speed selection in section 4. Check the revolutions per minute (RPM) to be sure that it doesn't exceed your machine limit. If it does then reduce Your Speed in section 4." data-id="element-980" data-mp="1wx56yq43groi" /><Instruction title="Section 6: Feed Rate Calculator" text="Choose Your Feed from the range given. All other boxes are calculated from this point." data-id="element-981" data-mp="43hcrb6xmi2" /></div></section>;
}
function Instruction({
  title,
  text
}: {
  title: string;
  text: string;
}) {
  return <section data-id="element-982" data-mp="1focgnwrfr978"><h3 className="font-bold text-brand-charcoal" data-id="element-983" data-mp="1fkeuf29ka1j4">{title}</h3><p data-id="element-984" data-mp="1ubnuk5b3b5ai">{text}</p></section>;
}